import React from "react";
import { NavLink } from "react-router-dom";

import Icons from "@dblimp/icomoon";
import navigationDefinitions from "../../schemas/definitions/navigation-schema.json";
import cart from "../../services/cart";

export interface SiteHeaderProps {
  name?: string;
  classes?: string;
}

interface SiteHeaderState {
  open: boolean;
  cartCount: number;
}

const navDefinitions = (navigationDefinitions as any).navigation as Record<
  string,
  { to?: string; href?: string; icon?: string; label?: string; name?: string }
>;

/** Menú del header desde `navigation-schema.json` (headerNav → internal `to`). */
const headerItems = ((navigationDefinitions as any).headerNav as string[])
  .map((key) => navDefinitions[key])
  .filter((entry) => entry && entry.to);

/**
 * Navbar del sitio. Vive al final del layout y con `position: sticky; bottom: 0`
 * queda pegado al borde inferior mientras se hace scroll.
 *
 * En móvil, un botón abre un `<aside>` (drawer lateral) con el menú.
 * El link de carrito muestra un badge con la cantidad (localStorage).
 */
export default class SiteHeader extends React.Component<SiteHeaderProps, SiteHeaderState> {
  static jsClass = "SiteHeader";
  static defaultProps: Partial<SiteHeaderProps> = {
    name: "mbHeader",
  };

  constructor(props: SiteHeaderProps) {
    super(props);
    this.state = { open: false, cartCount: 0 };
  }

  private unsubscribe?: () => void;

  componentDidMount() {
    this.setState({ cartCount: cart.count() });
    this.unsubscribe = cart.subscribe((items) =>
      this.setState({ cartCount: items.reduce((s, i) => s + i.qty, 0) })
    );
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  private toggle = () => this.setState((s) => ({ open: !s.open }));
  private close = () => this.setState({ open: false });

  private renderLabel(entry: { name?: string; label?: string }, count: number) {
    const isCart = entry.name === "carritoLink";
    return (
      <>
        <span className="ms-2">{entry.label}</span>
        {isCart && count > 0 && (
          <span className="mb-navbar__cart-badge">{count}</span>
        )}
      </>
    );
  }

  render(): React.ReactNode {
    const { classes } = this.props;
    const { open, cartCount } = this.state;

    return (
      <nav className={["mb-navbar", classes].filter(Boolean).join(" ")}>
        <div className="container mb-navbar__inner">
          <NavLink className="navbar-brand p-0" to="/" end onClick={this.close}>
            <img src="/assets/design/logo.png" alt="MANGIBONE" />
          </NavLink>

          <ul className="mb-navbar__nav">
            {headerItems.map((entry) => (
              <li key={entry.name}>
                <NavLink className="nav-link" to={entry.to as string} end={entry.name === "homeLink"}>
                  {entry.icon && <Icons icon={entry.icon} size={20} />}
                  {this.renderLabel(entry, cartCount)}
                </NavLink>
              </li>
            ))}
          </ul>

          <button
            className="mb-navbar__toggle"
            type="button"
            aria-label="Abrir menú"
            aria-expanded={open}
            aria-controls="mbMenuDrawer"
            onClick={this.toggle}>
            <span className="mb-navbar__bars" />
          </button>
        </div>

        <div
          className={"mb-drawer__backdrop" + (open ? " is-open" : "")}
          onClick={this.close}
          aria-hidden="true"
        />

        <aside
          id="mbMenuDrawer"
          className={"mb-drawer" + (open ? " is-open" : "")}
          role="dialog"
          aria-modal="true"
          aria-label="Menú">
          <button
            className="mb-drawer__close"
            type="button"
            aria-label="Cerrar menú"
            onClick={this.close}>
            ✕
          </button>
          <ul className="mb-drawer__nav">
            {headerItems.map((entry) => (
              <li key={entry.name}>
                <NavLink
                  className="mb-drawer__link"
                  to={entry.to as string}
                  end={entry.name === "homeLink"}
                  onClick={this.close}>
                  {entry.icon && <Icons icon={entry.icon} size={20} />}
                  {this.renderLabel(entry, cartCount)}
                </NavLink>
              </li>
            ))}
          </ul>
        </aside>
      </nav>
    );
  }
}
